import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFlash } from '../../context/flashcontext';
import api from '../../Api';
import documentAI from '../../services/documentAI';
import './aiDocumentUpload.style.css';

const AIDocumentUpload = ({
  userId,
  projectId,
  expectedDocumentType,
  onValidationComplete,
  onUploadSuccess
}) => {
  const { t, i18n } = useTranslation();
  const { showFlash } = useFlash();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [expiryDate, setExpiryDate] = useState('');

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check if it's an image or PDF
    const isImage = selectedFile.type.startsWith('image/');
    const isPDF = selectedFile.type === 'application/pdf';

    if (!isImage && !isPDF) {
      showFlash(t('documents.invalidFileType') || 'Alleen afbeeldingen (JPG, PNG) en PDF-bestanden zijn toegestaan.', 'error');
      return;
    }

    setFile(selectedFile);

    // Create preview for images only
    if (isImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      // For PDF, show a placeholder
      setPreview('pdf');
    }

    // Auto-validate with AI (only for images - PDFs need different handling)
    if (isImage) {
      await validateDocument(selectedFile);
    } else {
      // For PDFs, we can't do OCR but can still validate metadata
      setValidationResult({
        success: true,
        detectedType: expectedDocumentType || 'other',
        expirationDate: null,
        status: 'unknown',
        confidence: 50,
        suggestions: [{
          type: 'warning',
          message: 'PDF gedetecteerd. Handmatige verificatie aanbevolen.',
          messageEN: 'PDF detected. Manual verification recommended.',
          messagePL: 'PDF wykryty. Zalecana ręczna weryfikacja.'
        }]
      });
    }
  };

  const validateDocument = async (fileToValidate) => {
    setValidating(true);
    setValidationResult(null);

    try {
      const result = await documentAI.validateDocument(
        fileToValidate,
        expectedDocumentType
      );

      setValidationResult(result);

      if (onValidationComplete) {
        onValidationComplete(result);
      }
    } catch (error) {
      console.error('Validation error:', error);
      setValidationResult({
        success: false,
        error: error.message
      });
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !validationResult) return;

    // For PDFs, require manual document type selection
    if (file.type === 'application/pdf' && !selectedDocType) {
      showFlash(t('documents.selectDocType') || 'Selecteer een documenttype voor PDF bestanden.', 'warning');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_category', selectedDocType || validationResult.detectedType || expectedDocumentType || 'other');
      formData.append('expires_at', expiryDate || validationResult.expirationDate || '');

      // Determine upload endpoint based on whether it's user or project document
      const uploadEndpoint = projectId
        ? `/projects/${projectId}/documents/upload`
        : `/users/${userId}/documents/upload`;

      const response = await api.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;

      if (onUploadSuccess) {
        onUploadSuccess(data);
      }

      // Reset form
      setFile(null);
      setPreview(null);
      setValidationResult(null);
      setSelectedDocType('');
      setExpiryDate('');

      showFlash(t('documents.uploadSuccess') || 'Document succesvol geüpload!', 'success');
    } catch (error) {
      console.error('Upload error:', error);

      // Extract error message from response
      let errorMessage = t('documents.uploadError') || 'Uploaden mislukt';

      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const serverMessage = error.response.data?.error || error.response.data;

        if (status === 400) {
          if (typeof serverMessage === 'string') {
            if (serverMessage.includes('Invalid date format')) {
              errorMessage = t('documents.invalidDateFormat') || 'Ongeldige datumnotatie. Gebruik JJJJ-MM-DD.';
            } else if (serverMessage.includes('too large')) {
              errorMessage = t('documents.fileTooLargeServer') || 'Bestand is te groot. Maximum is 10MB.';
            } else if (serverMessage.includes('Invalid file')) {
              errorMessage = t('documents.invalidFile') || 'Ongeldig bestand. Alleen afbeeldingen en PDF zijn toegestaan.';
            } else {
              errorMessage = `${t('documents.uploadError')}: ${serverMessage}`;
            }
          }
        } else if (status === 413) {
          errorMessage = t('documents.payloadTooLarge') || 'Bestand is te groot voor de server. Kies een kleiner bestand.';
        } else if (status === 500) {
          errorMessage = t('documents.serverError') || 'Server fout. Probeer het later opnieuw.';
        } else {
          errorMessage = `${t('documents.uploadError')} (${status})`;
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = t('documents.networkError') || 'Netwerkfout. Controleer je internetverbinding.';
      } else {
        // Something else happened
        errorMessage = `${t('documents.uploadError')}: ${error.message}`;
      }

      showFlash(errorMessage, 'error');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return '#10b981';
      case 'expiringSoon': return '#f59e0b';
      case 'expired': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSuggestionMessage = (suggestion) => {
    const lang = i18n.language;
    if (lang === 'en' && suggestion.messageEN) return suggestion.messageEN;
    if (lang === 'pl' && suggestion.messagePL) return suggestion.messagePL;
    return suggestion.message;
  };

  return (
    <div className="ai-document-upload">
      <div className="upload-header">
        <h3>{t('documents.aiValidation')}</h3>
        <p className="upload-subtitle">
          {t('documents.aiValidationDescription') ||
           'Upload your document and our AI will automatically validate it'}
        </p>
      </div>

      {/* File Upload */}
      <div className="upload-section">
        <label htmlFor="document-upload" className="upload-label">
          {preview ? (
            <div className="preview-container">
              {preview === 'pdf' ? (
                <div className="pdf-preview">
                  <div className="pdf-icon">PDF</div>
                  <p className="pdf-name">{file?.name}</p>
                  <p className="pdf-hint">PDF document</p>
                </div>
              ) : (
                <>
                  <img src={preview} alt="Document preview" className="document-preview" />
                  <div className="change-file-overlay">
                    <span>{t('common.change') || 'Change file'}</span>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="upload-placeholder">
              <div className="upload-icon">+</div>
              <p>{t('documents.dragDropOrClick') || 'Click to upload or drag and drop'}</p>
              <p className="upload-hint">
                {expectedDocumentType ?
                  `${t('documents.expected')}: ${t(`documents.documentTypes.${expectedDocumentType}`)}` :
                  t('documents.anyType') || 'Any document type'}
              </p>
            </div>
          )}
        </label>
        <input
          id="document-upload"
          type="file"
          accept="image/*,application/pdf"
          onChange={handleFileSelect}
          className="file-input"
          disabled={validating || uploading}
        />
      </div>

      {/* Validation Progress */}
      {validating && (
        <div className="validation-progress">
          <div className="spinner"></div>
          <p>{t('documents.validating')}</p>
          <p className="progress-detail">
            {t('documents.runningAIChecks') || 'Running AI checks: OCR, type detection, expiry validation...'}
          </p>
        </div>
      )}

      {/* Validation Results */}
      {validationResult && !validating && (
        <div className="validation-results">
          <div className="result-header">
            <h4>{t('documents.validationComplete')}</h4>
            <div
              className={`confidence-badge ${
                validationResult.confidence > 70 ? 'confidence-high' :
                validationResult.confidence > 40 ? 'confidence-medium' : 'confidence-low'
              }`}
            >
              {validationResult.confidence}% {t('documents.confidence')}
            </div>
          </div>

          <div className="result-grid">
            {/* Document Type - Now editable */}
            <div className="result-item">
              <span className="result-label">{t('documents.type')}:</span>
              <select
                value={selectedDocType || validationResult.detectedType || ''}
                onChange={(e) => setSelectedDocType(e.target.value)}
                className="form-select-inline"
                title={t('documents.editDetectedType') || 'Klik om het gedetecteerde type te wijzigen'}
              >
                {projectId ? (
                  // Project document types
                  <>
                    <option value="offerte">Offerte</option>
                    <option value="tekening">Tekening</option>
                    <option value="werkbeschrijving">Werkbeschrijving</option>
                    <option value="leveringsbon">Leveringsbon</option>
                    <option value="vierkantemeterstaat">Vierkantemeter staat</option>
                    <option value="contract">Contract</option>
                    <option value="overig">Overig</option>
                  </>
                ) : (
                  // User document types
                  <>
                    <option value="KvK-uittreksel">KvK-uittreksel</option>
                    <option value="VCA-diploma">VCA-diploma</option>
                    <option value="Verzekeringspolis">Verzekeringspolis</option>
                    <option value="VOG">VOG</option>
                    <option value="ID-bewijs">ID-bewijs</option>
                    <option value="Contract">Contract</option>
                    <option value="Overig">Overig</option>
                  </>
                )}
              </select>
            </div>

            {/* Expiration Date - Now editable */}
            <div className="result-item">
              <span className="result-label">{t('documents.expiresAt')}:</span>
              <input
                type="date"
                value={expiryDate || validationResult.expirationDate || ''}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="form-input-inline"
                title={t('documents.editDetectedDate') || 'Klik om de gedetecteerde datum te wijzigen'}
              />
            </div>

            {/* Status */}
            <div className="result-item">
              <span className="result-label">{t('documents.status')}:</span>
              <span
                className="status-badge"
                style={{ backgroundColor: getStatusColor(validationResult.status) }}
              >
                {validationResult.status === 'unknown' ? t('documents.unknown') : t(`documents.${validationResult.status}`)}
              </span>
            </div>
          </div>

          {/* AI Suggestions */}
          {validationResult.suggestions && validationResult.suggestions.length > 0 && (
            <div className="suggestions-section">
              <h5>{t('documents.aiSuggestions')}</h5>
              {validationResult.suggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  className={`suggestion ${suggestion.type}`}
                >
                  <span className="suggestion-icon">
                    {suggestion.type === 'error' ? '❌' :
                     suggestion.type === 'warning' ? '⚠️' : '✅'}
                  </span>
                  <span className="suggestion-text">
                    {getSuggestionMessage(suggestion)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Extracted Text Preview (Collapsible) */}
          {validationResult.extractedText && (
            <details className="extracted-text">
              <summary>{t('documents.viewExtractedText') || 'View extracted text'}</summary>
              <pre className="text-preview">{validationResult.extractedText}</pre>
            </details>
          )}
        </div>
      )}

      {/* Manual Input for PDFs */}
      {file && validationResult && file.type === 'application/pdf' && (
        <div className="pdf-manual-input">
          <h5>📝 Handmatige Invoer (PDF)</h5>
          <p className="pdf-hint">Vul de gegevens handmatig in voor PDF documenten</p>

          <div className="form-group">
            <label htmlFor="docType" className="form-label">
              {t('documents.type')} <span className="required">*</span>
            </label>
            <select
              id="docType"
              value={selectedDocType}
              onChange={(e) => setSelectedDocType(e.target.value)}
              className="form-select"
              required
            >
              <option value="">Selecteer documenttype...</option>
              {projectId ? (
                // Project document types
                <>
                  <option value="offerte">Offerte</option>
                  <option value="tekening">Tekening</option>
                  <option value="werkbeschrijving">Werkbeschrijving</option>
                  <option value="leveringsbon">Leveringsbon</option>
                  <option value="vierkantemeterstaat">Vierkantemeter staat</option>
                  <option value="contract">Contract</option>
                  <option value="overig">Overig</option>
                </>
              ) : (
                // User document types
                <>
                  <option value="KvK-uittreksel">KvK-uittreksel</option>
                  <option value="VCA-diploma">VCA-diploma</option>
                  <option value="Verzekeringspolis">Verzekeringspolis</option>
                  <option value="VOG">VOG</option>
                  <option value="ID-bewijs">ID-bewijs</option>
                  <option value="Contract">Contract</option>
                  <option value="Overig">Overig</option>
                </>
              )}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="expiryDate" className="form-label">
              {t('documents.expiresAt')}
            </label>
            <input
              id="expiryDate"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="form-input"
            />
          </div>
        </div>
      )}

      {/* Upload Button */}
      {file && validationResult && (
        <>
          {file.type === 'application/pdf' && !selectedDocType && (
            <div className="warning-message">
              ⚠️ Selecteer een documenttype om te uploaden
            </div>
          )}
          <button
            className="btn-upload-final"
            onClick={handleUpload}
            disabled={uploading || validationResult.status === 'expired' || (file.type === 'application/pdf' && !selectedDocType)}
          >
            {uploading ? (
              <>
                <span className="spinner-small"></span>
                {t('documents.uploading') || 'Uploading...'}
              </>
            ) : (
              <>
                {t('documents.uploadDocument') || 'Upload Document'}
              </>
            )}
          </button>
        </>
      )}

      {validationResult && validationResult.status === 'expired' && (
        <div className="upload-blocked-message">
          ⚠️ {t('documents.cannotUploadExpired') || 'Cannot upload expired document. Please use a valid document.'}
        </div>
      )}
    </div>
  );
};

AIDocumentUpload.propTypes = {
  userId: PropTypes.number,
  projectId: PropTypes.number,
  expectedDocumentType: PropTypes.oneOf(['id', 'vog', 'insurance', 'contract', 'other', 'offerte', 'tekening', 'werkbeschrijving', 'leveringsbon', 'vierkantemeterstaat']),
  onValidationComplete: PropTypes.func,
  onUploadSuccess: PropTypes.func
};

export default AIDocumentUpload;

